import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import { getErrorMessage } from '../../../utils/api-response.util';
import { FeatureFormDialog } from './FeatureFormDialog';

export interface Feature {
  id: number;
  key: string;
  name: string;
  description: string | null;
}

export interface FeaturesTabService {
  listFeatures(): Promise<Feature[]>;
  createFeature(dto: { key: string; name: string; description?: string }): Promise<Feature>;
  updateFeature(id: number, dto: { name?: string; description?: string }): Promise<Feature>;
  deleteFeature(id: number): Promise<void>;
}

interface FeaturesTabProps {
  service: FeaturesTabService;
}

/**
 * Feature registry CRUD — shared code (not shared data): the client and expert
 * pages each pass their own isolated service (client-entitlements.service.ts /
 * expert-entitlements.service.ts), hitting fully separate backend tables.
 */
export const FeaturesTab: React.FC<FeaturesTabProps> = ({ service }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [deletingFeature, setDeletingFeature] = useState<Feature | null>(null);

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    try {
      setFeatures(await service.listFeatures());
    } catch {
      toastService.error('Не вдалося завантажити реєстр можливостей.');
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const handleCreate = () => {
    setEditingFeature(null);
    setFormOpen(true);
  };

  const handleEdit = (feature: Feature) => {
    setEditingFeature(feature);
    setFormOpen(true);
  };

  const handleSubmit = async (values: { key: string; name: string; description: string }) => {
    try {
      if (editingFeature) {
        await service.updateFeature(editingFeature.id, { name: values.name, description: values.description });
      } else {
        await service.createFeature({ key: values.key, name: values.name, description: values.description });
      }
      toastService.success('Збережено.');
      setFormOpen(false);
      fetchFeatures();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося зберегти можливість.'));
    }
  };

  const handleDelete = async () => {
    if (!deletingFeature) return;
    try {
      await service.deleteFeature(deletingFeature.id);
      toastService.success('Видалено.');
      setDeletingFeature(null);
      fetchFeatures();
    } catch (e) {
      toastService.error(getErrorMessage(e, 'Не вдалося видалити можливість.'));
    }
  };

  return (
    <Box>
      <PageCard>
        <PageToolbar
          filters={[]}
          extraActions={<Button variant="contained" onClick={handleCreate}>Додати можливість</Button>}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : features.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Можливостей ще не зареєстровано.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ключ</TableCell>
                  <TableCell>Назва</TableCell>
                  <TableCell>Опис</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => (
                  <TableRow key={feature.id} hover>
                    <TableCell><code>{feature.key}</code></TableCell>
                    <TableCell>{feature.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{feature.description || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => handleEdit(feature)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Видалити">
                        <IconButton size="small" color="error" onClick={() => setDeletingFeature(feature)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageCard>

      <FeatureFormDialog
        open={formOpen}
        feature={editingFeature}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmationDialog
        open={!!deletingFeature}
        onClose={() => setDeletingFeature(null)}
        onConfirm={handleDelete}
        title="Видалити можливість?"
        message={`"${deletingFeature?.name}" (${deletingFeature?.key}) буде прибрано з усіх планів, які її мали.`}
        confirmText="Видалити"
      />
    </Box>
  );
};
